import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const WEEKDAYS = ["Lu", "Ma", "Mé", "Xo", "Ve", "Sá", "Do"];
const MONTHS = [
  "Xaneiro", "Febreiro", "Marzo", "Abril", "Maio", "Xuño",
  "Xullo", "Agosto", "Setembro", "Outubro", "Novembro", "Decembro",
];

function startOfMonth(year, month) {
  return new Date(year, month, 1);
}

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

// Monday-first day offset (0 = Mon, 6 = Sun)
function monthStartOffset(year, month) {
  const day = new Date(year, month, 1).getDay(); // 0=Sun
  return (day + 6) % 7;
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isInRange(date, from, to) {
  if (!from || !to) return false;
  const d = date.getTime();
  return d > from.getTime() && d < to.getTime();
}

function parseDate(str) {
  if (!str) return null;
  const d = new Date(str);
  return isNaN(d) ? null : d;
}

function formatDate(date) {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function MonthGrid({ year, month, dateFrom, dateTo, hoverDate, onDayClick, onDayHover }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const totalDays = daysInMonth(year, month);
  const offset = monthStartOffset(year, month);

  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  const rangeEnd = dateTo || hoverDate;

  return (
    <div className="min-w-[17rem]">
      <p className="text-center text-sm font-semibold text-dark mb-3">
        {MONTHS[month]} {year}
      </p>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-xs font-medium text-muted py-1">
            {w}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7">
        {cells.map((day, idx) => {
          if (!day) {
            return <div key={`empty-${idx}`} />;
          }

          const date = new Date(year, month, day);
          date.setHours(0, 0, 0, 0);

          const isPast = date < today;
          const isStart = isSameDay(date, dateFrom);
          const isEnd = isSameDay(date, dateTo);
          const inRange = isInRange(date, dateFrom, rangeEnd);
          const isHoverEnd = isSameDay(date, hoverDate) && dateFrom && !dateTo;

          let cellClass =
            "relative flex items-center justify-center w-full aspect-square text-sm cursor-pointer select-none transition-colors";

          if (isPast) {
            cellClass += " opacity-40 cursor-not-allowed pointer-events-none";
          } else if (isStart || isEnd) {
            cellClass += " bg-dark text-white rounded-full z-10";
          } else if (isHoverEnd) {
            cellClass += " bg-subtle rounded-full";
          } else if (inRange) {
            cellClass += " bg-subtle";
          } else {
            cellClass += " hover:bg-subtle rounded-full text-dark";
          }

          // Range strip: flatten the start/end corners on the inside edge
          let wrapClass = "relative";
          if (!isPast) {
            if (isStart && rangeEnd && !isSameDay(dateFrom, rangeEnd)) {
              wrapClass += " after:absolute after:inset-y-0 after:right-0 after:w-1/2 after:bg-subtle after:-z-0";
            }
            if (isEnd && dateFrom && !isSameDay(dateFrom, dateTo)) {
              wrapClass += " after:absolute after:inset-y-0 after:left-0 after:w-1/2 after:bg-subtle after:-z-0";
            }
          }

          return (
            <div key={day} className={wrapClass}>
              <button
                type="button"
                className={cellClass}
                onClick={() => !isPast && onDayClick(date)}
                onMouseEnter={() => onDayHover(date)}
                onMouseLeave={() => onDayHover(null)}
                disabled={isPast}
              >
                {day}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DatePanel({ dateFrom, dateTo, onChange, onClose }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [hoverDate, setHoverDate] = useState(null);

  const parsedFrom = parseDate(dateFrom);
  const parsedTo = parseDate(dateTo);

  // Second month
  const nextMonthDate = new Date(viewYear, viewMonth + 1, 1);
  const nextYear = nextMonthDate.getFullYear();
  const nextMonth = nextMonthDate.getMonth();

  function handlePrev() {
    const prev = new Date(viewYear, viewMonth - 1, 1);
    setViewYear(prev.getFullYear());
    setViewMonth(prev.getMonth());
  }

  function handleNext() {
    const next = new Date(viewYear, viewMonth + 1, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  }

  function handleDayClick(date) {
    if (!parsedFrom || (parsedFrom && parsedTo)) {
      // Start new selection
      onChange({ dateFrom: formatDate(date), dateTo: null });
    } else {
      // Complete the range
      if (date < parsedFrom) {
        onChange({ dateFrom: formatDate(date), dateTo: formatDate(parsedFrom) });
      } else {
        onChange({ dateFrom: dateFrom, dateTo: formatDate(date) });
      }
    }
  }

  function handleClear() {
    onChange({ dateFrom: null, dateTo: null });
  }

  const sharedProps = {
    dateFrom: parsedFrom,
    dateTo: parsedTo,
    hoverDate,
    onDayClick: handleDayClick,
    onDayHover: setHoverDate,
  };

  return (
    <div className="absolute top-full mt-2 bg-white rounded-2xl shadow-card-hover border border-border p-4 z-50 animate-fade-in">
      {/* Navigation header */}
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={handlePrev}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-subtle transition-colors"
          aria-label="Mes anterior"
        >
          <ChevronLeft size={16} className="text-dark" />
        </button>

        {/* Months label row — hidden on mobile, title handled inside each grid */}
        <div className="hidden md:flex gap-8 text-sm font-semibold text-dark select-none pointer-events-none opacity-0">
          placeholder
        </div>

        <button
          type="button"
          onClick={handleNext}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-subtle transition-colors"
          aria-label="Mes seguinte"
        >
          <ChevronRight size={16} className="text-dark" />
        </button>
      </div>

      {/* Calendar grids */}
      <div className="flex flex-col md:flex-row gap-6">
        <MonthGrid year={viewYear} month={viewMonth} {...sharedProps} />
        <div className="hidden md:block w-px bg-border" />
        <div className="hidden md:block">
          <MonthGrid year={nextYear} month={nextMonth} {...sharedProps} />
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-border flex justify-end">
        <button
          type="button"
          onClick={handleClear}
          className="text-sm font-medium text-dark underline underline-offset-2 hover:text-muted transition-colors"
        >
          Borrar datas
        </button>
      </div>
    </div>
  );
}
